import React, { useEffect, useState } from 'react';
// i18next
import { useTranslation } from 'react-i18next';
// MUI
import { makeStyles } from '@mui/styles';
import { Button } from '@mui/material';
import config from 'config';
// Framer
import { motion } from 'framer-motion';
import { opacityAnimate } from 'utilities/Framer';
import useScrollIntoView from 'hooks/useScrollIntoView';

export const btnAnimate = {
  hidden: {
    opacity: 0,
    y: -100,
  },
  visible: (custom: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, delay: custom * 0.4 }, 
  })
}

const Banner:React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const { scrollTo } = useScrollIntoView();

  const [ativeBannerImg, setActiveBannerImg] = useState<number>(1);
  const [loadAllImages, setLoadAllImages] = useState<boolean>(false);

  useEffect(() => {
    const loadTimeout = setTimeout(() => setLoadAllImages(true), 1500);
    const bgInterval = setInterval(() => {
      setActiveBannerImg(prev => prev === 3 ? 1 : prev + 1);
    }, 3000)

    return () => {
      clearTimeout(loadTimeout);
      clearInterval(bgInterval);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <motion.section
      data-container="main"
      className={classes.block}
    >
      {(new Array(3).fill('')).map((image: string, index: number) => (index === 0 || loadAllImages) && (
        <img
          key={`banner-image-${index}`} alt=""
          src={`/images/banner/${index + 1}-1170.webp`}
          srcSet={`/images/banner/${index + 1}-800.webp 800w, /images/banner/${index + 1}-1170.webp 1170w`}
          sizes="100vw" width={1170} height={715}
          className={classes.bannerImg} style={{
            opacity: ativeBannerImg === (index + 1) ? 1 : 0,
            transition: loadAllImages ? undefined : 'none',
          }}
        />
      ))}
      <div className={classes.overlay} />
      <motion.div
        className={[classes.blockContent, 'container'].join(' ')}
        initial="hidden"
        whileInView="visible"
        viewport={{ amount: 0.2 }}
      >
        <motion.h1
          className={classes.title}
          variants={opacityAnimate}
          custom={1}
        >
          {t('homePage.banner.title')}
        </motion.h1>
        <motion.p
          className={classes.subtitle}
          variants={opacityAnimate}
          custom={2}
        >
          {t('homePage.banner.subtitle')}
        </motion.p>
        <motion.div
          className={classes.buttons}
          variants={btnAnimate}
          custom={3}
        >
          <Button
            variant="contained"
            href={`tel:${config.phoneNumber.replace(/\s+/g, '')}`}
          >
            {t('homePage.banner.callNow', { phone: config.phoneNumber })}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => scrollTo('contacts')}
          >
            {t('homePage.banner.bookService')}
          </Button>
          <Button variant="contained" color="secondary" onClick={() => scrollTo('services')}>
            {t('homePage.banner.ourServices')}
          </Button>
        </motion.div>
      </motion.div>
    </motion.section>
  );
}

export default Banner;

const useStyles = makeStyles({
  bannerImg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: '65% 50%',
    zIndex: -1,
    transition: 'opacity 2s',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.55)',
    zIndex: -1,
  },
  block: {
    position: 'relative',
    padding: '16px',
    height: 'calc(100vh - 85px)',
    '@media (min-width: 600px)': {
      padding: '32px'
    },
    '@media (min-width: 1240px)': {
      padding: '40px 32px'
    },
  },
  blockContent: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: '20px',
  },
  title: {
    color: '#0e073c',
    letterSpacing: '3px',
    textShadow: '0 0 3px #171f27',
    fontSize: '40px',
    lineHeight: '45px',
    zIndex: 1,
    position: 'relative',
    '@media (min-width: 600px)': {
      fontSize: '50px',
      lineHeight: '55px'
    },
    '@media (min-width: 1240px)': {
      fontSize: '64px',
      lineHeight: '70px',
    },
  },
  subtitle: {
    color: '#0e073c',
    backgroundColor: 'rgba(255, 255, 255, 0.82)',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: 500,
    maxWidth: '720px',
    zIndex: 1,
    position: 'relative',
    '@media (min-width: 1240px)': {
      fontSize: '20px',
    },
  },
  buttons: {
    paddingTop: '10px',
    '@media (min-width: 600px)': {
      paddingTop: '20px'
    },
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px'
  }
});
